// The app's address (05-routes-links.md): the browser's history, without a library. Every change is
// a history entry Back and Forward follow, except where an area replaces the address.
import { createContext, createElement, useContext, useSyncExternalStore, type AnchorHTMLAttributes } from 'react';
import { leave, readSession } from './session';

export const CHANGE = 'desyr:navigate';

const subscribe = (l: () => void) => {
  addEventListener('popstate', l);
  addEventListener(CHANGE, l);
  return () => {
    removeEventListener('popstate', l);
    removeEventListener(CHANGE, l);
  };
};

const current = () => location.pathname + location.search + location.hash;

/** The address a page drawn beside the current one sees (07, the page under a phone's room). */
export const Pinned = createContext<string | undefined>(undefined);

/** The current address (path, query and fragment), live. */
export const useAddress = () => {
  const live = useSyncExternalStore(subscribe, current);
  return useContext(Pinned) ?? live;
};

// Back never leaves the app's pages (07, v1.58.27): after a sign-in or an account switch the page has a
// guard entry under it with its address, and Back reaching it goes forward at once, drawing nothing.
// Two quick Backs can pass the guard before it goes forward: a sign-in page reached while signed in
// counts as the guard too.
const signInPage = () => /^\/(login|id\/done)(\/|$)/.test(location.pathname) && !!readSession();
addEventListener(
  'popstate',
  (e) => {
    if (!history.state?.guard && !signInPage()) return;
    e.stopImmediatePropagation();
    history.forward();
  },
  true,
);

/** The page `to` takes this entry's place, kept under it as the guard (07). */
export function guard(to = current()): void {
  history.replaceState({ guard: true }, '', to);
  history.pushState(null, '', to);
  dispatchEvent(new Event(CHANGE));
}
/** Opens `to` afresh in this entry, which stays as the guard (07; an account switch, 33). */
export const reopen = (to: string) => (leave(), history.replaceState({ guard: true }, '', to), location.reload());
// Reloaded on the guard: the page goes over it again.
if (history.state?.guard) guard();

export function navigate(to: string, { replace = false } = {}): void {
  if (to === current()) return;
  // Entries made inside desyr are marked, so a legal page's Back knows it can go back (31). Each entry
  // keeps the address it was opened from: the page under a phone's room (07).
  const { desyr, from } = replace ? (history.state ?? {}) : { desyr: true, from: current() };
  history[replace ? 'replaceState' : 'pushState']({ desyr: !!desyr, from }, '', to);
  dispatchEvent(new Event(CHANGE));
}

/** A link inside the app: a normal `a` element whose plain click navigates without a reload. */
export function Link({ to, replace, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean }) {
  return createElement('a', {
    href: to,
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navigate(to, { replace });
    },
    ...rest,
  });
}
